import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery
} from '@material-ui/core'
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { KeyboardDatePicker } from '@material-ui/pickers'
import clsx from 'clsx'
import { Formik } from 'formik'
import moment from 'moment'
import { CountriesProxy } from 'services'
import {
  CustomizedTooltip,
  CustomTextField,
  EllipseStatus,
  FormNotifyDialog,
  PrivacyPolicyContent,
  TermsAndConditionsContent
} from 'shared'
import { StyledSelect } from 'shared/EditableMultipleSelection/EditableMultipleSelection.style'
import { TermsAndConditionsDialog } from 'shared/TermsAndConditionsDialog'
import { downloadRulesDocument } from 'state/modules/events'
import { Transition } from 'utils'
import { STRING_EMPTY } from 'utils/constants'
import {
  filterAndSortCategories,
  filterCities,
  filterProvinces,
  getCountries
} from 'utils/functions'
import { showSnackbarError } from 'utils/snackbar'
import * as Yup from 'yup'

import { CustomField } from '../CustomField'
import { SectionBlock } from '../SectionBlock'

import { useStyles } from './InscriptionFormDialog.style'

const SALE_ORDER_STATES = {
  PAID: 'Paid',
  CANCELLED: 'Cancelled',
  PENDING: 'Pending',
  EXPIRED: 'Expired'
}

const INSCRIPTION_STATES = {
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  CANCELLED: 'Cancelled'
}

const observerOptions = [
  {
    value: INSCRIPTION_STATES.PENDING,
    label: <EllipseStatus status='yellow' title='Preinscripto' />
  },
  {
    value: INSCRIPTION_STATES.ACCEPTED,
    label: <EllipseStatus status='green' title='Inscripto' />
  },
  {
    value: INSCRIPTION_STATES.CANCELLED,
    label: <EllipseStatus status='red' title='Anulado' />
  },
  {
    value: SALE_ORDER_STATES.EXPIRED,
    label: <EllipseStatus status='grey' title='Vencido' />,
    optionHidden: true
  }
]

const genderList = [
  {
    label: 'Femenino',
    value: 'Female'
  },
  { label: 'Masculino', value: 'Male' },

  { label: 'No binario', value: 'NonBinary' }
]

const OTHER_OPTION = { label: 'Otra', value: 'other' }

const DIALOG_STATES = {
  MINI_DIALOG: 'miniDialog',
  FULL_DIALOG: 'fullDialog'
}

const generateOptionLabel = (option) =>
  `${option.number}${option.categoryId ? ` - ${option.categoryName}` : STRING_EMPTY}`

const InscriptionFormDialog = ({ open, onClose, title, data, formData = {}, onSubmit, state }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'))

  const [provinces, setProvinces] = useState([])
  const [cities, setCities] = useState([])
  const [categories, setCategories] = useState([])
  const [openPolicy, setOpenPolicy] = useState(false)
  const [openTerms, setOpenTerms] = useState(false)
  const [openNotifyErrorsDialog, setOpenNotifyErrorsDialog] = useState(false)
  const [dialogState, setDialogState] = useState(DIALOG_STATES.FULL_DIALOG)

  const localityEnabled = data.locality || data.localityOther
  const cityKey = localityEnabled
    ? { key: 'locality', text: 'localityOther' }
    : { key: 'areaLevel2', text: 'areaLevel2Other' }

  const { event, downloadRulesDocumentPending, bibNumbers } = useSelector((state) => state.events)

  const showNonBinaryOption = !!formData.items?.find(
    ({ eventCategory }) =>
      eventCategory.gender === OTHER_OPTION.value || eventCategory.gender === genderList[2].value
  )

  const formErrorDictionary = {
    firstName: { label: 'Nombre', order: 1 },
    lastName: { label: 'Apellido', order: 2 },
    idNumber: { label: 'N° de DNI', order: 3 },
    gender: { label: 'Género', order: 4 },
    birthdate: { label: 'Fecha de nacimiento', order: 5 },
    country: { label: 'País', order: 6 },
    areaLevel1: { label: 'Provincia', order: 7 },
    areaLevel2Option: { label: 'Ciudad', order: 8 },
    eMail: { label: 'E-mail', order: 9 },
    phone: { label: 'Teléfono', order: 10 },
    distance: { label: 'Distancia', order: 11 },
    category: { label: 'Categoría', order: 12 },

    ...formData.customFields?.reduce(
      (acc, e, index) => ({
        ...acc,
        [`customFields[${index}]`]: { label: e.name, order: 13 + index }
      }),
      {}
    ),

    termsAndConditions: {
      label: 'Términos y condiciones',
      order: 13 + formData.customFields?.length
    }
  }

  const getErrorsList = (errors) => {
    const termsAndConditions = errors.acceptedTermsAndConditions || errors.acceptedEventRules
    const areaLevel2Option = errors[cityKey.key] || errors[cityKey.text]

    return Object.keys({
      ...errors,

      ...errors.customFieldValues?.reduce(
        (acc, fieldValue, index) =>
          fieldValue
            ? {
                ...acc,
                [`customFields[${index}]`]: fieldValue
              }
            : acc,
        {}
      ),
      ...(areaLevel2Option ? { areaLevel2Option } : {}),
      ...(termsAndConditions ? { termsAndConditions } : {})
    })
      .map((x) => formErrorDictionary[x])
      .sort((a, b) => a?.order - b?.order)
  }

  const resetCountry = useCallback(() => {
    if (open) {
      onCountryChange()(data.country)
      onProvinceChange()(data.areaLevel1)
    }
  }, [data, open])

  useEffect(() => {
    updateCategories(data.distance.value, data.gender, data.birthdate, formData)
  }, [formData, data])

  useEffect(() => {
    resetCountry()
  }, [resetCountry])

  useEffect(() => {
    if (state) setDialogState(state)
  }, [state])

  const handleClickOpenPolicy = (e) => {
    e.preventDefault()
    setOpenPolicy(true)
  }

  const handleClosePolicy = () => setOpenPolicy(false)

  const handleClickOpenTerms = (e) => {
    e.preventDefault()
    setOpenTerms(true)
  }

  const handleCloseTerms = () => setOpenTerms(false)

  const filterGenderList = async (inputValue) =>
    genderList.filter(({ label }) => label.toLowerCase().includes(inputValue.toLowerCase()))

  const onCountryChange = (setValues) => async (options) => {
    try {
      setProvinces([])
      setCities([])

      if (setValues) {
        setValues((values) => {
          const resetUpdateProps =
            values.country.value !== options.value
              ? {
                  areaLevel1: STRING_EMPTY,
                  areaLevel2: STRING_EMPTY,
                  areaLevel2Other: STRING_EMPTY,
                  locality: STRING_EMPTY,
                  localityOther: STRING_EMPTY
                }
              : null

          return {
            ...values,
            ...resetUpdateProps,
            country: options
          }
        })
      }

      const proxy = new CountriesProxy()

      if (options.value) {
        const data = await proxy.getAreaLevel1(options.value)
        if (Array.isArray(data) && !!data.length) {
          setProvinces(data.map((d) => ({ value: d.id, label: d.name })))
        }
      }
    } catch (e) {
      showSnackbarError({ message: e })
    }
  }

  const onProvinceChange = (setValues) => async (options) => {
    setCities([])

    if (setValues) {
      setValues((values) => {
        const resetUpdateProps =
          values.areaLevel1.value !== options.value
            ? { [cityKey.key]: STRING_EMPTY, [cityKey.text]: STRING_EMPTY }
            : null

        return { ...values, ...resetUpdateProps, areaLevel1: options }
      })
    }

    const proxy = new CountriesProxy()

    if (options.value) {
      const data = localityEnabled
        ? await proxy.getLocality(options.value)
        : await proxy.getAreaLevel2(options.value)

      if (Array.isArray(data) && !!data.length) {
        setCities([...data.map((d) => ({ value: d.id, label: d.name })), OTHER_OPTION])
      }
    }
  }

  const onCityChange = (setValues) => async (options) =>
    setValues((values) => ({ ...values, [cityKey.key]: options, [cityKey.text]: STRING_EMPTY }))

  const updateCategories = (distance, gender, birthdate) => {
    const years =
      moment(birthdate).isValid() && moment(formData.validateAgeFrom).diff(birthdate, 'years')

    setCategories(
      filterAndSortCategories(distance, gender, years, formData).map(
        ({ eventCategory, eventCategoryId, state }) => ({
          label: eventCategory.name,
          value: eventCategoryId,
          state
        })
      )
    )
  }

  const onDistanceChange = (setFieldValue, values) => (option) => {
    if (values.distance.value !== option?.value) setFieldValue('category', STRING_EMPTY)

    setFieldValue('distance', option)
    updateCategories(option?.value, values.gender.value, values.birthdate)
  }

  const handleChangeGender = (values, setValues) => (gender) => {
    const category = values.gender.value === gender.value ? values.category : STRING_EMPTY

    setValues({ ...values, category, gender })

    updateCategories(values.distance.value, gender.value, values.birthdate)
  }

  const requiredProvince = (country) => country && country.value === event?.countryId

  const getStatusInscription = (state) => observerOptions.find((x) => x.value === state)

  const onChangeStatus = (setFieldValue) => async (options) => setFieldValue('state', options.value)

  const resetDistancesValues = () =>
    formData?.items
      .map((item) => ({
        label: item.eventDistance.name,
        value: item.eventDistanceId
      }))
      .reduce((acc, item) => {
        if (!acc.find((i) => i.value === item.value)) acc.push(item)
        return acc
      }, [])

  const searchDistance = (options, { inputValue }) =>
    options.filter((d) => d.label.toLowerCase().includes(inputValue.toLowerCase()))

  const searchCategory = (options, { inputValue }) =>
    options.filter((c) => c.label.toLowerCase().includes(inputValue.toLowerCase()))

  const handleDownloadRulesDocument = (e) => {
    if (event.rulesDocumentFile) {
      e.preventDefault()
      dispatch(downloadRulesDocument(event))
    }
  }

  const handleChangeBirthdate = (setValues, values) => (e) => {
    setValues({ ...values, birthdate: e, category: STRING_EMPTY })
    updateCategories(values.distance.value, values.gender.value, e)
  }

  const formValidate = (handleSubmit, isValid) => (e) => {
    e.preventDefault()

    if (isValid) handleSubmit(e)
    else setOpenNotifyErrorsDialog(true)
  }

  const handleOnClose = () => {
    setDialogState(isDesktop ? DIALOG_STATES.FULL_DIALOG : DIALOG_STATES.MINI_DIALOG)
    onClose()
  }

  const onInputChange = (setFieldValue) => (e, text) => setFieldValue('numberText', text)

  const renderOption = (option) => (
    <Typography className={clsx(classes.optionLabel, option.alert && classes.optionAlertLabel)}>
      {generateOptionLabel(option)}
    </Typography>
  )

  const values = {
    ...data,
    gender: genderList.find((e) => e.value === data.gender),
    areaLevel2Other: data.areaLevel2Other || STRING_EMPTY,
    comment: data.comment || STRING_EMPTY,
    state:
      data.state === INSCRIPTION_STATES.CANCELLED &&
      data.saleOrder?.state === SALE_ORDER_STATES.EXPIRED
        ? SALE_ORDER_STATES.EXPIRED
        : data.state,
    number: data.number
      ? {
          ...data.number,
          alert:
            data.number.distanceId !== data?.distance.value ||
            (data.number.categoryId && data.number.categoryId !== data?.category.value),
          value: data.number.id,
          label: generateOptionLabel(data.number)
        }
      : null,
    numberText: STRING_EMPTY
  }

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string()
          .max(256, 'El nombre es demasiado largo')
          .required('Ingrese su nombre')
          .trim(),
        acceptedTermsAndConditions: Yup.boolean().oneOf(
          [true],
          'Debe aceptar los términos y condiciones'
        ),
        country: Yup.mixed().required('Seleccione un país'),
        areaLevel1: Yup.mixed().when('country', {
          is: (country) => country?.value === event?.countryId,
          then: Yup.mixed().required('Seleccione una provincia')
        }),
        areaLevel2: Yup.mixed().when(['country', 'locality', 'localityOther'], {
          is: (country, locality, localityOther) =>
            !locality && !localityOther && country?.value === event?.countryId,
          then: Yup.mixed().required('Seleccione una ciudad')
        }),
        acceptedEventRules: Yup.boolean().oneOf([true], 'Debe aceptar las reglas del evento'),
        lastName: Yup.string()
          .max(256, 'El apellido es demasiado largo')
          .required('Ingrese su apellido')
          .trim(),
        eMail: Yup.string()
          .min(5, 'El e-mail es demasiado corto')
          .email('El e-mail no es válido')
          .max(254, 'El e-mail es demasiado largo')
          .required('Debe ingresar el e-mail'),

        gender: Yup.object().nullable().required('Debe elegir su género'),
        idNumber: Yup.string()
          .required('Debe ingresar su DNI')
          .min(6, 'El DNI debe poseer como mínimo 6 caracteres')
          .max(15, 'El DNI debe poseer como máximo 15 caracteres')
          .matches(/^[a-z0-9]+$/i, 'El DNI no debe tener caracteres especiales ni espacios'),
        birthdate: Yup.string()
          .nullable()
          .required('Seleccione su fecha de nacimiento')
          .test(
            'test-date',
            'No se admiten fechas futuras',
            (value) => !!value && moment(value).isBefore()
          )
          .test(
            'test-age',
            'La edad permitida es entre 2 y 120 años',
            (value) =>
              !!value && moment().diff(value, 'years') >= 2 && moment().diff(value, 'years') <= 120
          ),
        category: Yup.object().required('Debe elegir una categoría'),
        distance: Yup.object().required('Debe elegir una distancia'),
        phone: Yup.string()
          .nullable()
          .required('Debe ingresar un número de teléfono')
          .min(5, 'El número de teléfono debe tener al menos 5 caracteres')
          .max(20, 'El número de teléfono no puede tener más de 20 caracteres')
          .matches(/^[+]*(?:[0-9]?){5,20}[0-9]$/, 'El número de teléfono ingresado no es válido'),
        localityOther: Yup.string()
          .nullable()
          .when(['locality', 'country'], (locality, country, schema) =>
            localityEnabled &&
            locality?.value === OTHER_OPTION.value &&
            country?.value === event?.countryId
              ? schema.trim().required('Debe ingresar el nombre de su ciudad')
              : schema
          ),
        areaLevel2Other: Yup.string()
          .nullable()
          .when(['areaLevel2', 'country'], (areaLevel2, country, schema) =>
            !localityEnabled &&
            areaLevel2?.value === OTHER_OPTION.value &&
            country?.value === event?.countryId
              ? schema.trim().required('Debe ingresar el nombre de su ciudad')
              : schema
          ),
        customFieldValues: Yup.array().of(
          Yup.object().shape({
            fieldValue: Yup.mixed()
              .when('eventCustomField', (eventCustomField, schema) => {
                switch (eventCustomField.type) {
                  case 'Text':
                  case 'File':
                  case 'List':
                  case 'Boolean':
                    return Yup.string().nullable().trim()

                  case 'Number':
                    return Yup.number()
                      .typeError('Ingrese un número válido')
                      .max(999999999999999, 'Se puede ingresar hasta 15 dígitos')

                  default:
                    return schema
                }
              })
              .when('eventCustomField', (eventCustomField, schema) =>
                eventCustomField.isRequired ? schema.required('Este campo es requerido') : schema
              )
          })
        )
      }),
    [event, localityEnabled]
  )

  const activeMiniDialog = dialogState === DIALOG_STATES.MINI_DIALOG

  const dialogTitle = !activeMiniDialog ? title : 'Editar inscripción'

  const renderAltOption = (option) => (
    <Typography
      className={clsx(classes.optionLabel, option.state === 'error' && classes.optionAlertLabel)}>
      {option.label}
    </Typography>
  )

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      TransitionComponent={Transition}
      fullWidth={activeMiniDialog}
      BackdropProps={{
        classes: {
          root: classes.backDrop
        }
      }}
      fullScreen={!activeMiniDialog}
      PaperProps={{
        classes: {
          root: classes.dialog
        }
      }}>
      <div className={clsx(classes.headerContainer, activeMiniDialog && 'alt')}>
        <div className={classes.titleContainer}>
          <Typography variant='h5' color='primary' className={classes.title} title={dialogTitle}>
            {dialogTitle}
          </Typography>
        </div>
        <IconButton onClick={handleOnClose} color={activeMiniDialog ? 'primary' : 'secondary'}>
          <CloseIcon fontSize='large' />
        </IconButton>
      </div>
      <Formik
        initialValues={values}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onSubmit}>
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          isValid,
          setFieldValue,
          setValues
        }) => {
          const validationProvince = requiredProvince(values.country)
          const showOtherOption = values[cityKey.key]?.value === OTHER_OPTION.value
          const numberOptions = bibNumbers
            .map((x) => {
              const optionError =
                x.distanceId !== values?.distance.value ||
                (x.categoryId && x.categoryId !== values?.category.value)

              return {
                ...x,
                alert: optionError,
                state: optionError ? 'error' : STRING_EMPTY,
                value: x.id,
                label: generateOptionLabel(x)
              }
            })
            .filter((x) => !x.assignedAt || data.number?.id === x.id)
            .sort(
              (x, y) =>
                x.alert - y.alert ||
                x.number - y.number ||
                generateOptionLabel(x).localeCompare(generateOptionLabel(y))
            )

          return activeMiniDialog ? (
            <DialogContent>
              <form onSubmit={handleSubmit}>
                <div className={classes.inscriptionInfo}>
                  <Typography color='primary' variant='h6'>
                    {values.lastName} {values.firstName}
                  </Typography>

                  <Typography color='primary' variant='h6'>
                    D.N.I: {values.idNumber}
                  </Typography>
                </div>
                <div className={classes.comboxsContainer}>
                  <div className={classes.fieldContainer}>
                    <Autocomplete
                      id='distance-autocomplete'
                      options={resetDistancesValues()}
                      getOptionLabel={(x) => x.label}
                      onChange={(_, option) => onDistanceChange(setFieldValue, values)(option)}
                      filterOptions={searchDistance}
                      disableClearable
                      selectOnFocus
                      className={classes.combo}
                      getOptionSelected={(option, value) => option.value === value.value}
                      value={values.distance}
                      loadingText={<Typography align='center'> Cargando...</Typography>}
                      noOptionsText={<Typography align='center'> Sin opciones</Typography>}
                      clearText='Quitar todo'
                      openText='Abrir desplegable'
                      closeText='Cerrar desplegable'
                      classes={{
                        option: classes.option
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className={clsx(classes.altInput, classes.comboInput)}
                          name='distance'
                          label='Seleccione una distancia'
                          variant='outlined'
                          error={touched.distance && Boolean(errors.distance)}
                          helperText={errors.distance}
                          onBlur={handleBlur}
                        />
                      )}
                    />
                  </div>
                  <div className={classes.fieldContainer}>
                    <Autocomplete
                      id='category-autocomplete'
                      options={categories}
                      getOptionLabel={(x) => x.label ?? STRING_EMPTY}
                      onChange={(_, option) =>
                        setValues((values) => ({
                          ...values,
                          categoryId: option?.value || STRING_EMPTY,
                          category: option || STRING_EMPTY
                        }))
                      }
                      filterOptions={searchCategory}
                      className={classes.combo}
                      getOptionSelected={(option, value) => value && option.value === value.value}
                      value={values.category}
                      loadingText={<Typography align='center'> Cargando...</Typography>}
                      noOptionsText={<Typography align='center'> Sin opciones</Typography>}
                      clearText='Quitar todo'
                      openText='Abrir desplegable'
                      closeText='Cerrar desplegable'
                      classes={{
                        option: classes.option
                      }}
                      renderOption={renderAltOption}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className={clsx(classes.altInput, classes.comboInput)}
                          name='category'
                          label='Seleccione una categoría'
                          variant='outlined'
                          error={touched.category && Boolean(errors.category)}
                          helperText={errors.category}
                          onBlur={handleBlur}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className={classes.miniObservation}>
                  <div
                    className={clsx(
                      classes.formGroup,
                      classes.observationSelectFormGroup,
                      'center'
                    )}>
                    <Typography color='primary' variant='h5'>
                      Estado
                    </Typography>

                    <StyledSelect
                      value={getStatusInscription(values.state)}
                      defaultOptions={observerOptions}
                      colorOptions='primary'
                      filterOption={(option) =>
                        !option.data.optionHidden && values.state !== option.value
                      }
                      isSearchable={false}
                      onChange={onChangeStatus(setFieldValue)}
                      className={classes.select}
                    />
                  </div>
                </div>
                {!!data.number && (
                  <div className={classes.numberContainer}>
                    <Autocomplete
                      id='number-autocomplete'
                      options={numberOptions}
                      getOptionLabel={generateOptionLabel}
                      selectOnFocus
                      onInputChange={onInputChange(setFieldValue)}
                      onChange={(_, optionSelected) => setFieldValue('number', optionSelected)}
                      filterOptions={(options, { inputValue }) =>
                        options.filter((o) =>
                          generateOptionLabel(o)
                            .toLowerCase()
                            .trim()
                            .includes(inputValue.trim().toLowerCase())
                        )
                      }
                      className={classes.combo}
                      getOptionSelected={(option, value) => option.id === value.id}
                      value={values.number}
                      loadingText={<Typography align='center'> Cargando...</Typography>}
                      noOptionsText={<Typography align='center'> Sin opciones</Typography>}
                      clearText='Quitar todo'
                      openText='Abrir desplegable'
                      closeText='Cerrar desplegable'
                      classes={{
                        option: classes.option
                      }}
                      renderOption={renderOption}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className={clsx(classes.altInput, classes.comboInput)}
                          name='number'
                          label='Asignar número al deportista'
                          variant='outlined'
                          onBlur={handleBlur}
                        />
                      )}
                    />

                    {values.number && values.number.alert && (
                      <div className={classes.optionAlert}>
                        <ErrorIcon color='error' />
                        <Typography color='error'>
                          El número que elegiste no corresponde a la distancia/categoría del
                          deportista.
                        </Typography>
                      </div>
                    )}
                  </div>
                )}

                <div className={clsx(classes.saveContainer, activeMiniDialog && 'mini')}>
                  <Typography
                    variant='h6'
                    color='primary'
                    onClick={() => setDialogState(DIALOG_STATES.FULL_DIALOG)}
                    className={classes.changeDialogStateLink}>
                    Editar otros campos
                  </Typography>
                  <Button
                    color='primary'
                    onClick={formValidate(handleSubmit, isValid)}
                    endIcon={isSubmitting && <CircularProgress color='primary' size={16} />}
                    disabled={isSubmitting}
                    variant='contained'
                    className={clsx(classes.button, classes.saveButton)}>
                    Guardar
                  </Button>
                </div>
              </form>
            </DialogContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className={classes.observationContainer}>
                <div className={classes.observation}>
                  <div className={clsx(classes.formGroup, classes.observationSelectFormGroup)}>
                    <Typography color='primary' variant='h5'>
                      Estado
                    </Typography>
                    <StyledSelect
                      value={getStatusInscription(values.state)}
                      defaultOptions={observerOptions}
                      colorOptions='primary'
                      filterOption={(option) =>
                        !option.data.optionHidden && values.state !== option.value
                      }
                      isSearchable={false}
                      onChange={onChangeStatus(setFieldValue)}
                      className={classes.select}
                    />
                  </div>
                  <div className={clsx(classes.observationTextFieldContainer, classes.formGroup)}>
                    <Typography color='primary' variant='h5'>
                      Observaciones de inscripción
                    </Typography>
                    <TextField
                      variant='outlined'
                      placeholder='Escriba aquí las observaciones que sean necesarias.'
                      className={classes.textAreaEdit}
                      color='primary'
                      name='comment'
                      multiline
                      onChange={handleChange}
                      value={values.comment}
                    />
                  </div>
                </div>
                {!!data.number && (
                  <div className={clsx(classes.observation, 'alt')}>
                    <div className={classes.assignNumberFormGroup}>
                      <Autocomplete
                        id='number-autocomplete'
                        options={numberOptions}
                        getOptionLabel={generateOptionLabel}
                        selectOnFocus
                        onInputChange={onInputChange(setFieldValue)}
                        onChange={(_, optionSelected) => setFieldValue('number', optionSelected)}
                        filterOptions={(options, { inputValue }) =>
                          options.filter((o) =>
                            generateOptionLabel(o)
                              .toLowerCase()
                              .trim()
                              .includes(inputValue.trim().toLowerCase())
                          )
                        }
                        className={classes.combo}
                        getOptionSelected={(option, value) => option.id === value.id}
                        value={values.number}
                        loadingText={<Typography align='center'> Cargando...</Typography>}
                        noOptionsText={<Typography align='center'> Sin opciones</Typography>}
                        clearText='Quitar todo'
                        openText='Abrir desplegable'
                        closeText='Cerrar desplegable'
                        classes={{
                          option: classes.option
                        }}
                        renderOption={renderOption}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className={clsx(classes.altInput, classes.comboInput)}
                            name='number'
                            label='Asignar número al deportista'
                            variant='outlined'
                            onBlur={handleBlur}
                          />
                        )}
                      />

                      {values.number && values.number.alert && (
                        <div className={clsx(classes.optionAlert, 'full')}>
                          <ErrorIcon color='error' />
                          <Typography color='error'>
                            El número que elegiste no corresponde a la distancia/categoría del
                            deportista.
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className={classes.sectionsContainer}>
                <SectionBlock>
                  <div className={classes.formGroup}>
                    <Typography color='primary' variant='h6'>
                      Nombre *
                    </Typography>
                    <TextField
                      variant='outlined'
                      size='small'
                      name='firstName'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={!!errors.firstName && touched.firstName}
                      helperText={errors.firstName}
                      fullWidth
                      value={values.firstName}
                      placeholder='Ingrese su nombre'
                      autoComplete='off'
                    />
                  </div>
                  <div className={classes.formGroup}>
                    <Typography color='primary' variant='h6'>
                      Apellido *
                    </Typography>
                    <TextField
                      variant='outlined'
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.lastName && touched.lastName}
                      helperText={errors.lastName}
                      placeholder='Ingrese su apellido'
                      size='small'
                      name='lastName'
                      fullWidth
                      autoComplete='off'
                    />
                  </div>
                  <div className={classes.separatorContainer}>
                    <div className={classes.formGroupContainer}>
                      <div className={classes.formGroup}>
                        <Typography color='primary' variant='h6'>
                          N° de DNI *
                        </Typography>
                        <TextField
                          variant='outlined'
                          size='small'
                          onBlur={handleBlur}
                          error={!!errors.idNumber && touched.idNumber}
                          helperText={errors.idNumber}
                          onChange={handleChange}
                          placeholder='********'
                          value={values.idNumber}
                          name='idNumber'
                          fullWidth
                          autoComplete='off'
                        />
                      </div>
                      <div className={clsx(classes.fieldContainer, classes.formGroup)}>
                        <Typography color='primary' variant='h6'>
                          Género *
                        </Typography>
                        <StyledSelect
                          cacheOptions
                          defaultOptions={genderList}
                          className={classes.select}
                          defaultValue={values.gender}
                          onBlur={handleBlur}
                          loadOptions={filterGenderList}
                          name='gender'
                          placeholder='Seleccione una opción'
                          colorOptions='secondary'
                          onChange={handleChangeGender(values, setValues)}
                          filterOption={(option) =>
                            option.value !== 'NonBinary' || showNonBinaryOption
                          }
                        />
                        {errors.gender && (
                          <Typography
                            variant='caption'
                            color={touched.gender ? 'error' : 'initial'}>
                            {errors.gender}
                          </Typography>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={classes.formGroupContainer}>
                    <div className={classes.formGroup}>
                      <Typography color='primary' variant='h6'>
                        Fecha de nacimiento *
                      </Typography>
                      <KeyboardDatePicker
                        className={`${classes.input} datePicker`}
                        format='DD/MM/YYYY'
                        autoOk
                        variant='inline'
                        name='birthdate'
                        onBlur={handleBlur}
                        margin='none'
                        id='date-picker-inline'
                        inputVariant='outlined'
                        error={!!errors.birthdate && touched.birthdate}
                        helperText={errors.birthdate}
                        onChange={handleChangeBirthdate(setValues, values)}
                        style={{ width: '100%' }}
                        value={values.birthdate}
                        KeyboardButtonProps={{
                          'aria-label': 'seleccione fecha'
                        }}
                        placeholder='ddmmaaaa'
                      />
                    </div>
                    <div className={clsx(classes.fieldContainer, classes.formGroup)}>
                      <Typography color='primary' variant='h6'>
                        País *
                      </Typography>
                      <StyledSelect
                        cacheOptions
                        defaultOptions
                        className={classes.select}
                        value={values.country}
                        onBlur={handleBlur}
                        loadOptions={getCountries()}
                        name='country'
                        placeholder='Seleccione su país'
                        colorOptions='secondary'
                        onChange={onCountryChange(setValues)}
                      />
                      {errors.country && (
                        <Typography variant='caption' color={touched.country ? 'error' : 'initial'}>
                          {errors.country}
                        </Typography>
                      )}
                    </div>
                  </div>
                  <div className={classes.formGroupContainer}>
                    {validationProvince && (
                      <>
                        <div className={clsx(classes.formGroup, classes.fieldContainer)}>
                          <Typography variant='h6' color='primary'>
                            Provincia *
                          </Typography>
                          <StyledSelect
                            cacheOptions
                            className={classes.select}
                            value={values.areaLevel1}
                            defaultOptions={provinces}
                            placeholder='Seleccione su provincia'
                            loadOptions={filterProvinces(provinces)}
                            onBlur={handleBlur}
                            colorOptions='secondary'
                            name='areaLevel1'
                            onChange={onProvinceChange(setValues)}
                          />
                          {errors.areaLevel1 && (
                            <Typography
                              variant='caption'
                              color={touched.areaLevel1 ? 'error' : 'initial'}>
                              {errors.areaLevel1}
                            </Typography>
                          )}
                        </div>
                        {values.areaLevel1 && (
                          <div className={classes.citiesContainer}>
                            <div
                              className={clsx(
                                classes.formGroup,
                                classes.fieldContainer,
                                showOtherOption && classes.optionOtherContainer
                              )}>
                              <Typography variant='h6' color='primary'>
                                Ciudad de Residencia *
                              </Typography>
                              <StyledSelect
                                cacheOptions
                                className={classes.select}
                                value={values[cityKey.key]}
                                defaultOptions={cities}
                                placeholder='Seleccione su ciudad'
                                loadOptions={filterCities(cities)}
                                onBlur={handleBlur}
                                colorOptions='secondary'
                                name={cityKey.key}
                                onChange={onCityChange(setValues)}
                              />
                              {errors[cityKey.key] && (
                                <Typography
                                  variant='caption'
                                  color={touched[cityKey.key] ? 'error' : 'initial'}>
                                  {errors[cityKey.key]}
                                </Typography>
                              )}
                            </div>
                            {showOtherOption && (
                              <div className={clsx(classes.formGroup, classes.otherOptionText)}>
                                <TextField
                                  variant='outlined'
                                  color='primary'
                                  size='small'
                                  autoComplete='off'
                                  fullWidth
                                  onChange={handleChange}
                                  name={cityKey.text}
                                  placeholder='Ingrese su ciudad'
                                  onBlur={handleBlur}
                                  errors={touched[cityKey.text] && !!errors[cityKey.text]}
                                  helperText={errors[cityKey.text]}
                                  value={values[cityKey.text]}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className={classes.formGroupContainer}>
                    <div className={classes.formGroup}>
                      <Typography color='primary' variant='h6'>
                        E-mail *
                      </Typography>
                      <TextField
                        variant='outlined'
                        size='small'
                        value={values.eMail}
                        name='eMail'
                        placeholder='martindelpotrozena@gmail.com'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.eMail && touched.eMail}
                        helperText={errors.eMail}
                        fullWidth
                        autoComplete='off'
                      />
                    </div>
                    <div className={classes.formGroup}>
                      <Typography color='primary' variant='h6'>
                        Teléfono *
                      </Typography>
                      <CustomTextField
                        variant='outlined'
                        size='small'
                        value={values.phone}
                        onBlur={handleBlur}
                        error={!!errors.phone && touched.phone}
                        helperText={errors.phone}
                        onChange={handleChange}
                        name='phone'
                        fullWidth
                        className={classes.phone}
                        autoComplete='off'
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <CustomizedTooltip
                                position='left-end'
                                isTopMost
                                title='El Organizador utilizará este número para enviarte información sobre el evento.'>
                                {({ handleTooltip }) => (
                                  <IconButton onClick={handleTooltip}>
                                    <InfoIcon color='primary' />
                                  </IconButton>
                                )}
                              </CustomizedTooltip>
                            </InputAdornment>
                          )
                        }}
                      />
                      <Typography className={classes.helperTextPhone}>
                        Cód. país + cód. de área sin cero + N° tel sin el 15, sin espacios ni
                        guiones.
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color='primary'
                            checked={values.hasWhatsApp}
                            onChange={(e) => setFieldValue('hasWhatsApp', e.target.checked)}
                            name='hasWhatsApp'
                            onBlur={handleBlur}
                          />
                        }
                        label={<Typography variant='h6'>Este teléfono tiene WhatsApp</Typography>}
                      />
                    </div>
                  </div>
                </SectionBlock>
                <SectionBlock title='Distancia y categoría'>
                  <div className={classes.comboxsContainer}>
                    <div className={classes.fieldContainer}>
                      <StyledSelect
                        cacheOptions
                        placeholder='Seleccione una distancia'
                        name='distance'
                        defaultOptions={resetDistancesValues()}
                        onBlur={handleBlur}
                        onChange={onDistanceChange(setFieldValue, values)}
                        colorOptions='secondary'
                        value={values.distance}
                        className={classes.select}
                        loadOptions={searchDistance}
                      />
                      {errors.distance && (
                        <Typography
                          variant='caption'
                          color={touched.distance ? 'error' : 'initial'}>
                          {errors.distance}
                        </Typography>
                      )}
                    </div>
                    <div className={classes.fieldContainer}>
                      <StyledSelect
                        cacheOptions
                        placeholder='Seleccione una categoría'
                        defaultOptions={categories}
                        onBlur={handleBlur}
                        onChange={(e) =>
                          setValues((values) => ({
                            ...values,
                            categoryId: e.value,
                            category: e
                          }))
                        }
                        value={values.category}
                        colorOptions='secondary'
                        name='category'
                        className={classes.select}
                        loadOptions={searchCategory}
                      />
                      {errors.category && (
                        <Typography
                          variant='caption'
                          color={touched.category ? 'error' : 'initial'}>
                          {errors.category}
                        </Typography>
                      )}
                    </div>
                  </div>
                </SectionBlock>
                {formData.customFields.map((field, index) => (
                  <SectionBlock
                    key={index}
                    title={`${field.name} ${field.isRequired ? '*' : STRING_EMPTY}`}
                    body={field.helperText}>
                    <CustomField
                      {...field}
                      value={
                        values.customFieldValues[index]
                          ? values.customFieldValues[index].fieldValue
                          : STRING_EMPTY
                      }
                      error={
                        Array.isArray(errors.customFieldValues)
                          ? errors?.customFieldValues[index]?.fieldValue
                          : STRING_EMPTY
                      }
                      touched={
                        Array.isArray(touched.customFieldValues)
                          ? touched?.customFieldValues[index]?.fieldValue
                          : STRING_EMPTY
                      }
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      fieldId={`customFieldValues[${index}].eventCustomFieldId`}
                      fieldName={`customFieldValues[${index}].fieldValue`}
                      handleBlur={handleBlur}
                    />
                  </SectionBlock>
                ))}
                <SectionBlock title='Términos y condiciones'>
                  <div className={classes.checkContainer}>
                    {(event.rulesUrl || event.rulesDocumentFile) && (
                      <>
                        <FormControlLabel
                          onChange={handleChange}
                          name='acceptedEventRules'
                          onBlur={handleBlur}
                          control={
                            <Checkbox
                              color='primary'
                              checked={values.acceptedEventRules === true}
                            />
                          }
                          label={
                            <Typography className={classes.tosParagraph}>
                              Acepto el&nbsp;
                              <a
                                href={event.rulesUrl}
                                rel='noopener noreferrer'
                                target='_blank'
                                onClick={handleDownloadRulesDocument}>
                                reglamento del evento.&nbsp;
                                {downloadRulesDocumentPending && (
                                  <CircularProgress size={14} color='inherit' />
                                )}
                              </a>
                            </Typography>
                          }
                        />
                        <Typography variant='caption' color='error'>
                          {errors.acceptedEventRules}
                        </Typography>
                      </>
                    )}
                    <FormControlLabel
                      onChange={handleChange}
                      name='acceptedTermsAndConditions'
                      onBlur={handleBlur}
                      control={
                        <Checkbox
                          color='primary'
                          checked={values.acceptedTermsAndConditions === true}
                        />
                      }
                      label={
                        <Typography variant='body1' className={classes.tosParagraph}>
                          Acepto las&nbsp;
                          <Link to='#' onClick={handleClickOpenTerms}>
                            Condiciones de uso
                          </Link>
                          &nbsp;y la&nbsp;
                          <Link to='#' onClick={handleClickOpenPolicy}>
                            Política de privacidad.
                          </Link>
                        </Typography>
                      }
                    />
                    <TermsAndConditionsDialog
                      handleClose={handleCloseTerms}
                      open={openTerms}
                      content={TermsAndConditionsContent}
                      title='Condiciones de uso'
                    />
                    <TermsAndConditionsDialog
                      handleClose={handleClosePolicy}
                      open={openPolicy}
                      content={PrivacyPolicyContent}
                      title='Política de privacidad'
                    />
                    <Typography variant='caption' color='error'>
                      {errors.acceptedTermsAndConditions}
                    </Typography>
                  </div>
                </SectionBlock>
                <div className={classes.submitContainer}>
                  <IconButton onClick={handleOnClose} className={classes.backButton}>
                    <ArrowBackIcon size='medium' color='primary' />
                  </IconButton>
                  <div className={classes.saveContainer}>
                    <Button
                      color='primary'
                      onClick={formValidate(handleSubmit, isValid)}
                      endIcon={isSubmitting && <CircularProgress color='primary' size={16} />}
                      disabled={isSubmitting}
                      variant='contained'
                      className={clsx(classes.button, classes.saveButton)}>
                      Guardar
                    </Button>
                    <FormNotifyDialog
                      open={openNotifyErrorsDialog}
                      onClose={() => setOpenNotifyErrorsDialog(false)}
                      title='NOTIFICACIÓN FORMULARIO INCOMPLETO'
                      subtitle='Para poder editar la pre-inscripción el formulario debe estar completo y no tener errores, revisa los siguientes campos:'
                      errors={getErrorsList(errors)}
                    />
                  </div>
                </div>
              </div>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export default InscriptionFormDialog
